import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface PrioritySettingsFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
}

export function PrioritySettingsForm({ formValues, onFieldChange }: PrioritySettingsFormProps) {
  const leistungsumfangOptions = [
    { id: 'top_leistung', label: 'TOP-Leistung' },
    { id: 'preis_leistung', label: 'Preis-/Leistung' },
    { id: 'guenstigste_praemie', label: 'Günstigste Prämie' },
  ];

  const kundenverbindungOptions = [
    { id: 'einzelproduktberatung', label: 'Einzelproduktberatung' },
    { id: 'gesamtkundenbetreuung', label: 'Gesamtkundenbetreuung' },
  ];

  const serviceIntervallOptions = [
    { id: 'jaehrlich', label: 'jährlich' },
    { id: 'halbjaehrlich', label: 'halbjährlich' },
    { id: 'vierteljaehrlich', label: 'vierteljährlich' },
    { id: 'nach_bedarf', label: 'nach Bedarf' },
  ];

  const rating = formValues.erfahrungen_rating || 0;

  const handleCheckboxChange = (group: string, id: string, checked: boolean) => {
    const currentValues = formValues[group] || [];
    if (checked) {
      onFieldChange(group, [...currentValues, id]);
    } else {
      onFieldChange(group, currentValues.filter((v: string) => v !== id));
    }
  };

  const isChecked = (group: string, id: string) => {
    return (formValues[group] || []).includes(id);
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Erfahrungen </span>
          <span className="text-primary">& Prioritäten</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1 font-handwriting">
          Ihr maßgeschneidertes Konzept.
        </p>
      </div>

      {/* Two-column grid for Leistungsumfang and Kundenverbindung */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Leistungsumfang */}
        <div className="space-y-3">
          <h3 className="font-semibold text-foreground">Leistungsumfang</h3>
          <div className="space-y-2">
            {leistungsumfangOptions.map((option) => (
              <div key={option.id} className="flex items-center gap-3">
                <Checkbox
                  id={option.id}
                  checked={isChecked('leistungsumfang', option.id)}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('leistungsumfang', option.id, !!checked)
                  }
                />
                <Label htmlFor={option.id} className="cursor-pointer text-sm">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Kundenverbindung */}
        <div className="space-y-3">
          <h3 className="font-semibold text-foreground">Kundenverbindung</h3>
          <div className="space-y-2">
            {kundenverbindungOptions.map((option) => (
              <div key={option.id} className="flex items-center gap-3">
                <Checkbox
                  id={option.id}
                  checked={isChecked('kundenverbindung', option.id)}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('kundenverbindung', option.id, !!checked)
                  }
                />
                <Label htmlFor={option.id} className="cursor-pointer text-sm">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service-Intervall */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">Service-Intervall</h3>
        <div className="space-y-2">
          {serviceIntervallOptions.map((option) => (
            <div key={option.id} className="flex items-center gap-3">
              <Checkbox
                id={option.id}
                checked={isChecked('service_intervall', option.id)}
                onCheckedChange={(checked) => 
                  handleCheckboxChange('service_intervall', option.id, !!checked)
                }
              />
              <Label htmlFor={option.id} className="cursor-pointer text-sm">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Star Rating */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          Ihre Erfahrungen und Erwartungen?
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => onFieldChange('erfahrungen_rating', star)}
                className="p-0.5 focus:outline-none"
              >
                <Star
                  className={cn(
                    "h-6 w-6 transition-colors",
                    star <= rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-muted-foreground/30"
                  )}
                />
              </button>
            ))}
          </div>
        </h3>
      </div>

      {/* Notes */}
      <Card className="border-2 border-muted rounded-xl">
        <CardHeader className="pb-2">
          <span className="font-semibold text-foreground">Notizen:</span>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formValues.prioritaeten_notizen || ''}
            onChange={(e) => onFieldChange('prioritaeten_notizen', e.target.value)}
            placeholder="Ihre Notizen..."
            className="min-h-[150px] border-0 resize-none focus-visible:ring-0 p-0"
          />
        </CardContent>
      </Card>
    </div>
  );
}
